import { Opportunity as OpportunityConst } from '@axiom/const';
import {
  CondensedLabel,
  CondensedLarge,
  DarkTheme,
  Flashy,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  SmallHeader,
  useApi,
  CardSection,
  Card,
  IconButton,
} from '@axiom/ui';
import type { Opportunity } from '@axiom/validation';
import React from 'react';

import { DateUtil } from '../../utils/date-util';
import { TaxonomyApi } from '../../api/taxonomy-api';

import { OpportunityProfileHeaderSalesforceSync } from './OpportunityProfileHeaderSalesforceSync';

export const OpportunityProfileHeader = ({
  opportunity = null,
}: {
  opportunity?: Opportunity | null;
}) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  if (!opportunity) return null;

  // Closed (Won/Lost) opps get a gray well with dark text; active/open opps get
  // a blue well, which needs light text (DarkTheme).
  const isClosed =
    opportunity.stageCode === 'W' || opportunity.stageCode === 'L';
  const isClosedWon = opportunity.stage === OpportunityConst.CLOSED_WON;
  const industryValue = opportunity.account?.industryValue;
  const industry = industryValue
    ? taxonomy.industries[industryValue]?.displayValue
    : undefined;

  const industryWidth = isClosedWon ? 2 : 3;

  const content = (
    <Layout position="space-between middle" verticalGutter="8px">
      <LayoutItem fluid>
        <Layout position="left middle" horizontalGutter="20px" stretched>
          <LayoutItem
            background={isClosed ? 'panelGray' : 'panelLight'}
            borderRadius="4px"
            name="stage-chip"
          >
            <Gutter horizontal="8px" vertical="4px">
              <Layout direction="vertical" position="center middle">
                <Flashy color="panelBlue" bold>
                  STAGE
                </Flashy>
                <SmallHeader name="STAGE_CODE">
                  <Flashy color="panelBlue">
                    {opportunity.stageCode || '?'}
                  </Flashy>
                </SmallHeader>
              </Layout>
            </Gutter>
          </LayoutItem>
          <LayoutItem fluid>
            <Grid>
              <GridRow verticalAlign="top">
                <GridColumn width={5}>
                  <CondensedLabel>Opportunity Name</CondensedLabel>
                </GridColumn>
                <GridColumn width={3}>
                  <Gutter gutterType="margin" top="-8px">
                    <Layout position="left middle" horizontalGutter="4px">
                      <CondensedLabel>Account Name</CondensedLabel>
                      <IconButton
                        name="AFT_TOOLTIP_ICON"
                        variation="minimal"
                        pattern={isClosed ? 'primary' : 'secondary'}
                        inverse={!isClosed}
                        icon="avatar"
                        title="Visible in Axiom for Talent"
                      />
                    </Layout>
                  </Gutter>
                </GridColumn>
                <GridColumn width={industryWidth}>
                  <CondensedLabel>Industry</CondensedLabel>
                </GridColumn>
                <GridColumn width={1}>
                  <CondensedLabel>Close Date</CondensedLabel>
                </GridColumn>
                {isClosedWon ? (
                  <GridColumn width={1}>
                    <CondensedLabel>Matter ID</CondensedLabel>
                  </GridColumn>
                ) : null}
              </GridRow>
              <GridRow>
                <GridColumn width={5}>
                  <CondensedLarge name="OPPORTUNITY_NAME">
                    {opportunity.jobName || '--'}
                  </CondensedLarge>
                </GridColumn>
                <GridColumn width={3}>
                  <CondensedLarge name="ACCOUNT_NAME">
                    {opportunity.accountName || '--'}
                  </CondensedLarge>
                </GridColumn>
                <GridColumn width={industryWidth}>
                  <CondensedLarge name="INDUSTRY">
                    {industry || '--'}
                  </CondensedLarge>
                </GridColumn>
                <GridColumn width={1}>
                  <CondensedLarge name="CLOSE_DATE">
                    {DateUtil.displayDate(opportunity.salesCloseDate) || '--'}
                  </CondensedLarge>
                </GridColumn>
                {isClosedWon ? (
                  <GridColumn width={1}>
                    <CondensedLarge name="MATTER_ID">
                      {opportunity.integrationId || '--'}
                    </CondensedLarge>
                  </GridColumn>
                ) : null}
              </GridRow>
            </Grid>
          </LayoutItem>
        </Layout>
      </LayoutItem>
      <LayoutItem>
        {opportunity.selfService && !opportunity.isSynced ? (
          <OpportunityProfileHeaderSalesforceSync
            name="OPPORTUNITY_PROFILE_HEADER_SALESFORCE_SYNC"
            opportunity={opportunity}
          />
        ) : null}
      </LayoutItem>
    </Layout>
  );

  return (
    <Gutter horizontal="4px">
      <Card shadow="default">
        <CardSection
          background={isClosed ? 'light' : 'blue'}
          name="OPPORTUNITYPROFILEHEADER"
        >
          {isClosed ? (
            content
          ) : (
            <DarkTheme
              name="OPPORTUNITY_PROFILE_HEADER_DARK_THEME"
              backgroundName="none"
            >
              {content}
            </DarkTheme>
          )}
        </CardSection>
      </Card>
    </Gutter>
  );
};
